// general
// material ui
import { Grid, MenuItem, Select, Typography } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { MTableToolbar } from 'material-table';
// other
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';

// api
import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import {
  ResourceActive,
  ResourceActiveDisabled,
  ResourceActiveEnabled,
  ResourceActiveUnknown,
} from '~/src/dash/api/console-api';
import {
  deleteAdserverOrder,
  getAdserverOrders,
  IAdserverOrder,
  updateAdserverOrder,
} from '~/src/dash/api/order';
// components
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import {
  IAdserverOrderRowData,
  IListTableColumn,
  ListTable,
} from '~/src/dash/components/list-table';
import { MenuButton } from '~/src/dash/components/menu-button';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
// utils
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteOrderSettingsClone,
  RouteOrderSettingsExisting,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import {
  ChipCompleted,
  ChipDelivering,
  ChipGoalMet,
  ChipInactive,
  ChipPending,
  computeCampaignStatus,
  ECampaignStatus,
} from '~/src/dash/utils/campaign-status';
import { publicaColors } from '~/src/dash/utils/colors';
import { getDateWithOffset, parseDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { obfuscateCompanyName, obfuscateOrderName } from '~/src/dash/utils/obfuscate';
import {
  IPermissionCheck,
  IS_AUTHORIZED,
  notAllowedEmptyIPermissionCheck,
} from '~/src/dash/utils/permission_service';
import { routeName } from '~/src/dash/utils/routes';
// views
import { FilterActiveChip } from '~/src/dash/views/campaign/campaign-settings-list-view';
import { RowButton } from '~/src/dash/views/component-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IHistoryProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IWithStylesProps {}

interface IState {
  campaigns: IAdserverCampaign[];
  count: number;
  loading: boolean;
  readAuthorization: IPermissionCheck;
  writeAuthorization: IPermissionCheck;
  orders: IAdserverOrder[];
  snackbarMessage: string;
  snackbarError: boolean;
  updating: boolean;
  // used for top left dropdown
  orderFilter: ResourceActive;
  // start date filters
  startDateFilterStart?: moment.Moment;
  startDateFilterEnd?: moment.Moment;
  // advertiser names for filtering
  advertiserIDAndNames: string[];
  advertiserFilter: string;
}

export const OrderListView = decorate(
  {
    redux: ({ user, session }: IStore) => {
      return {
        user,
        session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      campaigns: [] as IAdserverCampaign[],
      count: 0,
      loading: true,
      readAuthorization: notAllowedEmptyIPermissionCheck,
      writeAuthorization: notAllowedEmptyIPermissionCheck,
      orders: [] as IAdserverOrder[],
      snackbarMessage: '',
      snackbarError: false,
      updating: false,
      orderFilter: ResourceActiveEnabled,
      startDateFilterStart: undefined,
      startDateFilterEnd: undefined,
      advertiserIDAndNames: [] as string[],
      advertiserFilter: 'Show All',
    };

    componentDidMount() {
      const { user } = this.props;

      this.setState({
        readAuthorization: IS_AUTHORIZED(['read_campaign'], user.user),
        writeAuthorization: IS_AUTHORIZED(['write_campaign'], user.user),
      });

      // get all of the orders when the component mounts (preloaded with line items)
      this.getOrdersAndFindEarliestStart();
    }

    getOrdersAndFindEarliestStart = () => {
      const { session } = this.props;

      this.setState(
        {
          updating: true,
        },
        () => {
          getAdserverOrders(session.publisher.ID)
            .then((orders: IAdserverOrder[]) => {
              const advertiserIDAndNamesMap = {} as { [key: string]: boolean };
              advertiserIDAndNamesMap['Show All'] = true;
              for (let idx = 0; idx < orders.length; idx++) {
                const advertiser = orders[idx].Advertiser;
                const advertiserID = orders[idx].AdvertiserID;
                if (advertiser) {
                  advertiserIDAndNamesMap[`[${advertiserID}] ${advertiser.Name}`] = true;
                }
                // if the order has all inactive line items or no line items, we can skip
                // the process of finding the earliest date
                const allInactive = orders[idx].LineItems.every(
                  (lineItem) => parseInt(lineItem.Active, 10) === ResourceActiveDisabled,
                );
                if (!orders[idx].LineItems.length || allInactive) {
                  continue;
                }

                // determine the line item with the earliest date
                const firstLineItem = orders[idx].LineItems[0];
                let earliestDate = getDateWithOffset(
                  moment(firstLineItem.StartDate),
                  firstLineItem.Timezone,
                );

                for (let jdx = 1; jdx < orders[idx].LineItems.length; jdx++) {
                  const currLineItem = orders[idx].LineItems[jdx];
                  const lineItemActive = parseInt(currLineItem.Active, 10);
                  if (lineItemActive !== ResourceActiveEnabled) {
                    continue;
                  }

                  // updates the order's start date to be the earliest line item's start date
                  if (
                    getDateWithOffset(
                      moment(currLineItem.StartDate),
                      currLineItem.Timezone,
                    ).isBefore(earliestDate)
                  ) {
                    earliestDate = getDateWithOffset(
                      moment(currLineItem.StartDate),
                      currLineItem.Timezone,
                    );
                  }
                }

                orders[idx].CreatedAt = earliestDate.format();
              }
              this.setState({
                orders,
                loading: false,
                updating: false,
                advertiserIDAndNames: Object.keys(advertiserIDAndNamesMap),
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarError: true,
                snackbarMessage: 'Error loading orders. Please try again.',
                orders: [] as IAdserverOrder[],
                loading: false,
                updating: false,
              });
            });
        },
      );
    };

    // ****************************************** //
    // ******* Event Notification Handler ******* //
    // ****************************************** //

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    handleSetUpdating = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    // ****************************************** //
    // ************ Table Navigation ************ //
    // ****************************************** //

    getItemSettingsRoute = (id: number): string => {
      return getRoutePath(RouteOrderSettingsExisting, extractRouteParams(this.props), {
        order: id.toString(),
      });
    };

    // navigates to clone existing
    // disabled for now until we can clone the child line items
    handleCloneItem = (id: number): void => {
      navigate(RouteOrderSettingsClone, extractRouteParams(this.props), {
        order: id.toString(),
      });
    };

    // deletes existing order
    handleDeleteItem = (orderIDStr: string) => {
      const orderID = parseInt(orderIDStr, 10);
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteAdserverOrder(orderID, this.props.session.publisher.ID)
            .then(() => {
              let name;
              const orders = this.state.orders.filter((order: IAdserverOrder) => {
                if (order.ID === orderID) {
                  name = order.Name;
                }
                return order.ID !== orderID;
              });
              this.setState({
                orders,
                snackbarMessage: `Successfully deleted order: [${orderID}] ${name}`,
                snackbarError: false,
                updating: false,
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting order',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );

      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // ****************************************** //
    // ************* Table Handlers ************* //
    // ****************************************** //

    handleActiveItem = (orderID: number) => {
      const { session } = this.props;
      const { orders } = this.state;
      // find the order that is going to be updated
      let activeStatus;
      for (let idx = 0; idx < this.state.orders.length; idx++) {
        if (orders[idx].ID === orderID) {
          activeStatus =
            orders[idx].Active === ResourceActiveDisabled
              ? ResourceActiveEnabled
              : ResourceActiveDisabled;
        }
      }

      // create a new order with the ID and the updated status
      if (activeStatus !== undefined) {
        const updatedOrder = {
          ID: orderID,
          Active: activeStatus,
        } as IAdserverOrder;

        // since we are only updating the active field, this is a partial update rather than a full update
        // from this, we will always make the line item children the same active status as the order
        updateAdserverOrder(updatedOrder, session.publisher.ID, false, true)
          .then(() => {
            // after updating the order, get the update order and update status
            this.getOrdersAndFindEarliestStart();
            this.setState({
              snackbarMessage: 'Successfully saved order',
              snackbarError: false,
            });
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'Failed to save order',
              snackbarError: true,
            });
          });
      }
      // the active modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    handleChangeOrderFilter = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        orderFilter: parseInt(target.value, 10) || 0,
      });
    };

    handleChangeAdvertiserFilter = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        advertiserFilter: target.value,
      });
    };

    compareDates = (rowData1: IAdserverOrderRowData, rowData2: IAdserverOrderRowData) => {
      return rowData1.StartDate.isBefore(rowData2.StartDate) ? -1 : 1;
    };

    calcLineItemStatuses = (rowData: IAdserverOrderRowData): any => {
      // if loading, just return an empty grid since we want targeting returned
      if (this.state.loading) {
        return <Grid />;
      }
      const lineItems = rowData.LineItems;
      const campaignStatuses = {
        Delivering: 0,
        Ready: 0,
        NotStartedYet: 0,
        Completed: 0,
        Untargeted: 0,
        Unknown: 0,
        MediafileError: 0,
        GoalMet: 0,
      };
      // calculate the sums of the line item statuses
      for (const lineItem of lineItems) {
        const campaignStatus = computeCampaignStatus(
          lineItem,
          this.props.session.publisher.Timezone,
          parseInt(lineItem.CRONQuantityCount, 10) || 0,
        );
        if (campaignStatus === ECampaignStatus.Delivering) {
          campaignStatuses.Delivering += 1;
        } else if (campaignStatus === ECampaignStatus.Ready) {
          campaignStatuses.Ready += 1;
        } else if (campaignStatus === ECampaignStatus.NotStartedYet) {
          campaignStatuses.NotStartedYet += 1;
        } else if (campaignStatus === ECampaignStatus.Completed) {
          campaignStatuses.Completed += 1;
        } else if (campaignStatus === ECampaignStatus.Untargeted) {
          campaignStatuses.Untargeted += 1;
        } else if (campaignStatus === ECampaignStatus.Unknown) {
          campaignStatuses.Unknown += 1;
        } else if (campaignStatus === ECampaignStatus.MediafileError) {
          campaignStatuses.MediafileError += 1;
        } else if (campaignStatus === ECampaignStatus.GoalMet) {
          campaignStatuses.GoalMet += 1;
        }
      }
      // iterate over the key-values and filter out any with value of 0
      const filteredStatuses = Object.entries(campaignStatuses).filter((status) => {
        const value = status[1];
        return value !== 0;
      });
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid
            container
            direction='row'
            justify={filteredStatuses.length === 1 ? 'center' : 'space-between'}
            alignItems='center'
            spacing={1}
          >
            {filteredStatuses.map((status) => {
              const [key, value] = status;
              let element;
              if (key === 'Delivering') {
                element = (
                  <ChipDelivering
                    clickable={false}
                    label={`Delivering: ${value}`}
                    style={{ marginRight: 5 }}
                  />
                );
              } else if (key === 'Ready') {
                element = (
                  <ChipPending
                    clickable={false}
                    label={`Ready: ${value}`}
                    style={{ marginRight: 5 }}
                  />
                );
              } else if (key === 'NotStartedYet') {
                element = (
                  <ChipPending
                    clickable={false}
                    label={`Not Started: ${value}`}
                    style={{ marginRight: 5 }}
                  />
                );
              } else if (key === 'Completed') {
                element = (
                  <ChipCompleted
                    clickable={false}
                    label={`Completed: ${value}`}
                    style={{ marginRight: 5 }}
                  />
                );
              } else if (key === 'Untargeted') {
                element = (
                  <ChipInactive
                    clickable={false}
                    label={`Untargeted: ${value}`}
                    style={{ marginRight: 5 }}
                  />
                );
              } else if (key === 'Unknown') {
                element = (
                  <ChipInactive
                    clickable={false}
                    label={`Unknown: ${value}`}
                    style={{ marginRight: 5 }}
                  />
                );
              } else if (key === 'MediafileError') {
                element = (
                  <ChipDelivering
                    clickable={false}
                    label={`Mediafile Error: ${value}`}
                    style={{ marginRight: 5 }}
                  />
                );
              } else {
                element = (
                  <ChipGoalMet
                    clickable={false}
                    label={`Goal Met: ${value}`}
                    style={{ marginRight: 5 }}
                  />
                );
              }
              return (
                <Grid item xs={5}>
                  {element}
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      );
    };

    // filter menu helper functions

    handleStartDateFilterChange = ({ start, end }: IDateRangeChangeEvent) => {
      this.setState({
        startDateFilterStart: start,
        startDateFilterEnd: end,
      });
    };

    handleClearFilters = () => {
      this.setState({
        startDateFilterStart: undefined,
        startDateFilterEnd: undefined,
        advertiserFilter: 'Show All',
        orderFilter: ResourceActiveEnabled,
      });
    };

    // filter menu render functions

    renderMenuTitle = () => {
      const activeFilterCount = this.countActiveFilters();
      return (
        <Grid container direction='row' alignItems='center' spacing={2}>
          <Grid item>
            <Typography
              style={{
                marginLeft: 10,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: getColorTheme('secondaryButtonText'),
              }}
            >
              FILTERS
            </Typography>
          </Grid>
          <Grid item>
            <FilterListIcon
              style={{
                fontSize: '1rem',
                fontWeight: 500,
                color: getColorTheme('secondaryButtonText'),
              }}
            />
          </Grid>
          {activeFilterCount !== 0 && (
            <Grid item xs={2} onClick={this.handleClearFilters}>
              <FilterActiveChip label={`${activeFilterCount} ACTIVE FILTERS`} />
            </Grid>
          )}
        </Grid>
      );
    };

    renderStatusRow = () => {
      const { orderFilter } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>Status</Typography>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <Select
                value={orderFilter}
                onChange={this.handleChangeOrderFilter}
                style={{ fontSize: '14px' }}
              >
                <MenuItem value={ResourceActiveEnabled}>Active</MenuItem>
                <MenuItem value={ResourceActiveDisabled}>Inactive</MenuItem>
                <MenuItem value={ResourceActiveUnknown}>Show All</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderAdvertiserRow = () => {
      const { advertiserIDAndNames } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>Advertisers</Typography>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <Select
                value={this.state.advertiserFilter}
                onChange={this.handleChangeAdvertiserFilter}
                style={{ fontSize: '14px' }}
              >
                {advertiserIDAndNames.map((name: string) => (
                  <MenuItem value={name}>{name}</MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderStartDateRow = () => {
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>Start Date Range</Typography>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <DateTimeRangeComponent
                timezone={'UTC'}
                disableTime={true}
                start={this.state.startDateFilterStart}
                end={this.state.startDateFilterEnd}
                onChange={this.handleStartDateFilterChange}
                enableClearSelection={true}
                enableSelectAll={true}
                enablePresets={true}
                smallCalendar={true}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderClearFilterRow = () => {
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2} onClick={this.handleClearFilters}>
              <Typography
                style={{
                  fontSize: 14,
                  paddingLeft: 10,
                  fontWeight: 500,
                  color: getColorTheme('secondaryButtonText'),
                }}
              >
                Clear Filters
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderMenuButton = () => {
      const menuTitleRow = {
        closeOnClick: true,
        render: this.renderMenuTitle,
      };
      // status dropdown
      const statusRow = {
        closeOnClick: false,
        render: this.renderStatusRow,
      };
      // advertiser dropdown
      const advertiserRow = {
        closeOnClick: false,
        render: this.renderAdvertiserRow,
      };
      // start date dropdown
      const startDateRow = {
        closeOnClick: false,
        render: this.renderStartDateRow,
      };
      // clear filters
      const clearFilterRow = {
        closeOnClick: false,
        render: this.renderClearFilterRow,
      };
      const menuButtonItems = [
        menuTitleRow,
        statusRow,
        advertiserRow,
        startDateRow,
        clearFilterRow,
      ];
      return (
        <MenuButton
          titleRender={this.renderMenuTitle}
          items={menuButtonItems}
          minWidth={600}
          enableGreyFirstItem={true}
        />
      );
    };

    countActiveFilters = (): number => {
      let activeFilterCount = 0;
      if (
        this.state.orderFilter === ResourceActiveEnabled ||
        this.state.orderFilter === ResourceActiveDisabled
      ) {
        activeFilterCount++;
      }
      if (this.state.advertiserFilter !== 'Show All') {
        activeFilterCount++;
      }
      if (this.state.startDateFilterStart || this.state.startDateFilterEnd) {
        activeFilterCount++;
      }
      return activeFilterCount;
    };

    // filters orders from filter dropdown, when filter is added, also update countActiveFilters
    filterOrders = (orders: IAdserverOrder[]) => {
      const { orderFilter } = this.state;
      return orders.filter((order: IAdserverOrder) => {
        // active filter
        if (orderFilter === ResourceActiveEnabled && order.Active !== ResourceActiveEnabled) {
          return false;
        } else if (
          this.state.orderFilter === ResourceActiveDisabled &&
          order.Active !== ResourceActiveDisabled
        ) {
          return false;
        }

        // start date start filter, remove all orders with start date before filter
        if (this.state.startDateFilterStart) {
          if (parseDate(order.CreatedAt || '').isBefore(this.state.startDateFilterStart)) {
            return false;
          }
        }
        // start date end filter, remove all orders with start date after filter
        if (this.state.startDateFilterEnd) {
          if (parseDate(order.CreatedAt || '').isAfter(this.state.startDateFilterEnd)) {
            return false;
          }
        }

        // advertiser name filter
        if (this.state.advertiserFilter !== 'Show All') {
          let advertiserWithID = 'Unknown';
          if (order && order.Advertiser) {
            advertiserWithID = order.Advertiser
              ? `[${order.AdvertiserID}] ${obfuscateCompanyName(
                  order.Advertiser,
                  this.props.user.user.Username,
                  order.Advertiser.Name,
                )}`
              : '';
          }
          // if there is no advertiser but there's a filter or if the name does  not match
          if (this.state.advertiserFilter !== advertiserWithID) {
            return false;
          }
        }

        return true;
      });
    };

    // renders the custom tool bar for the main list
    renderCustomToolbar = (props: any) => {
      const { classes } = this.props;
      const { user } = this.props;

      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };

      return (
        <Grid item className={classes.toolbarRow}>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid item>
              <Grid
                container
                direction='column'
                justify='flex-start'
                alignItems='flex-start'
                spacing={0}
              >
                {/* grid with MTableToolbarWrapper class is required for auto focusing search upon loading the list table component */}
                <Grid item className='MTableToolbarWrapper'>
                  <MTableToolbar {...copyProps} />
                </Grid>
                <Grid item>{this.renderMenuButton()}</Grid>
              </Grid>
            </Grid>
            <Grid item>
              <RowButton
                right={
                  <ModelMediumButton
                    variant='contained'
                    color='primary'
                    style={{ paddingLeft: 15, paddingRight: 15 }}
                    component={Link}
                    to={`/app/pub/${user.publisher.ID}/orders/new`}
                  >
                    + New Order
                  </ModelMediumButton>
                }
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const { loading, orders, updating, snackbarError, snackbarMessage } = this.state;

      const orderColumns: IListTableColumn[] = [
        {
          field: 'Active',
          title: 'Active',
          renderType: 'activeswitchmodal',
          searchable: false,
          maxWidth: '75px',
        },
        { field: 'ID', title: 'ID', renderType: 'normal', searchable: true, hidden: true },
        {
          field: 'Name',
          field2: 'IDString',
          title: 'Order Name',
          renderType: 'overunderlink',
          searchable: true,
          minWidth: '250px',
        },
        {
          field: 'CustomRender',
          title: 'Line Items',
          render: (rowData: IAdserverOrderRowData) => this.calcLineItemStatuses(rowData),
          searchable: false,
          sorting: false,
          minWidth: '245px',
        },
        {
          field: 'Advertiser',
          title: 'Advertiser',
          renderType: 'normal',
          searchable: true,
          maxWidth: '200px',
        },
        {
          field: 'StartDate',
          title: 'Start Date',
          renderType: 'date',
          searchable: false,
          customSort: this.compareDates,
          maxWidth: '125px',
        },
        {
          field: 'Impressions',
          title: 'Impressions',
          renderType: 'bignumber',
          searchable: false,
          maxWidth: '100px',
        },
        { field: 'Actions', title: 'Actions', renderType: 'deleteclone', searchable: false },
      ];

      const filteredOrders = this.filterOrders(orders);

      const orderData: IAdserverOrderRowData[] = filteredOrders.map((order: IAdserverOrder) => {
        const advertiserWithID = order.Advertiser
          ? `[${order.AdvertiserID}] ${obfuscateCompanyName(
              order.Advertiser,
              this.props.user.user.Username,
              order.Advertiser.Name,
            )}`
          : '';
        return {
          ID: order.ID,
          IDString: `ID: ${order.ID}`,
          Active: order.Active === ResourceActiveEnabled,
          Name: obfuscateOrderName(order, this.props.user.user.Username, order.Name),
          LineItems: order.LineItems,
          Advertiser: advertiserWithID,
          StartDate: parseDate(order.CreatedAt || ''),
          Impressions: order.LineItems.map((lineItem) => lineItem.CRONQuantityCount).reduce(
            (accum, impressions) => {
              return accum + parseInt(impressions, 10);
            },
            0,
          ),
          DisableDelete: order.LineItems.length > 0,
        };
      });

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largeHorizontalPadding verticalPadding>
            <Grid item>
              <ListTable
                title=''
                columns={orderColumns}
                data={orderData}
                disableSnackbar={true}
                isLoading={loading}
                isUpdating={updating}
                handleActiveItem={this.handleActiveItem}
                handleSetUpdating={this.handleSetUpdating}
                handleDeleteItem={this.handleDeleteItem}
                getItemSettingRoute={this.getItemSettingsRoute}
                handleCloneItem={this.handleCloneItem}
                renderCustomToolbar={this.renderCustomToolbar}
                readRoleName={'read_campaign'}
                writeRoleName={'write_campaign'}
                disableDeleteMsg={'Unable to delete. Please remove all line items.'}
              />
            </Grid>
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
